// src/components/Notifications/PushNotificationManager.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermissionAndGetToken,
  onForegroundMessage,
} from '../../services/fcmService';

/**
 * Synthesizes a soft, clear 2-tone audio chime using the Web Audio API.
 * Requires zero external audio files.
 */
const playNotificationChime = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const audioCtx = new AudioContextClass();
    const now = audioCtx.currentTime;

    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();

    osc.type = 'sine';
    // Two-tone chime: D5 (587.33 Hz) -> A5 (880 Hz)
    osc.frequency.setValueAtTime(587.33, now);
    osc.frequency.setValueAtTime(880, now + 0.12);

    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

    osc.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start(now);
    osc.stop(now + 0.45);
  } catch (e) {
    // Audio autoplay might be restricted before first user interaction
  }
};

const PushNotificationManager = () => {
  const navigate = useNavigate();
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem('fcm_prompt_dismissed') === 'true'
  );

  const unsubscribeRef = useRef(null);

  // 1. Initial support check & auto-sync if already granted
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const supported = await isNotificationSupported();
      if (!mounted) return;
      setIsSupported(supported);

      if (!supported) return;

      const currentPermission = getNotificationPermission();
      setPermission(currentPermission);

      // If already granted, silently ensure token is registered and up-to-date in Firestore
      if (currentPermission === 'granted') {
        requestNotificationPermissionAndGetToken().catch(() => {});
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, []);

  // 2. Handle incoming foreground notifications
  const handleForegroundNotification = useCallback(
    (payload) => {
      // eslint-disable-next-line no-console
      console.log('[FCM] Foreground notification received:', payload);

      const title = payload.notification?.title || payload.data?.title || 'ECabs Admin Alert';
      const body =
        payload.notification?.body ||
        payload.data?.body ||
        payload.data?.message ||
        'You have a new message.';

      const threadId = payload.data?.threadId;
      const tripId = payload.data?.tripId;
      const isSupportChatAllowed = localStorage.getItem('isSupportChat') !== 'false';

      // Suppress support chat alerts if support chat is disabled for this admin
      if (!isSupportChatAllowed && (threadId || payload.data?.type === 'support_chat')) {
        return;
      }
      const url =
        payload.data?.url ||
        (threadId
          ? `/app/support-chat?threadId=${threadId}`
          : tripId
            ? `/app/trips/view/${tripId}`
            : null);

      // Play soft chime alert
      playNotificationChime();

      // Show interactive React-Toastify notification
      toast.info(
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <strong style={{ fontSize: '13.5px', color: '#0f172a' }}>{title}</strong>
          <span style={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.4 }}>{body}</span>
          {url && (
            <div style={{ marginTop: '6px' }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => navigate(url)}
                sx={{
                  fontSize: '11px',
                  textTransform: 'none',
                  padding: '2px 10px',
                  minHeight: '24px',
                }}
              >
                {threadId ? 'Open Chat' : 'View'}
              </Button>
            </div>
          )}
        </div>,
        {
          position: 'top-right',
          autoClose: 7000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // If document is hidden (user on another browser tab), also show native notification
      if (
        typeof document !== 'undefined' &&
        document.hidden &&
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        try {
          const nativeNotification = new Notification(title, {
            body,
            icon: payload.notification?.icon || '/assets/images/logo.png',
            tag: threadId || 'ecabs-alert',
          });
          nativeNotification.onclick = () => {
            window.focus();
            if (url) navigate(url);
            nativeNotification.close();
          };
        } catch (e) {
          // Native notification creation fallback
        }
      }
    },
    [navigate]
  );

  // 3. Attach onForegroundMessage listener
  useEffect(() => {
    let active = true;

    onForegroundMessage((payload) => {
      if (active) {
        handleForegroundNotification(payload);
      }
    }).then((unsub) => {
      if (active) {
        unsubscribeRef.current = unsub;
      } else if (typeof unsub === 'function') {
        unsub();
      }
    });

    return () => {
      active = false;
      if (typeof unsubscribeRef.current === 'function') {
        unsubscribeRef.current();
      }
    };
  }, [handleForegroundNotification]);

  // 4. Request notification permission handler
  const handleEnableNotifications = async () => {
    setEnabling(true);
    try {
      const result = await requestNotificationPermissionAndGetToken();
      setPermission(result.permission);

      if (result.token) {
        toast.success('Push notifications enabled! You will now receive real-time alerts.');
      } else if (result.permission === 'granted' && !result.token) {
        toast.error(
          'Permission granted, but Firebase token generation failed. Check console for details.'
        );
      } else if (result.permission === 'denied') {
        toast.warn('Notifications are blocked. Please enable them in your browser site settings.');
      }
    } catch (error) {
      toast.error('Failed to enable push notifications.');
    } finally {
      setEnabling(false);
    }
  };

  const handleDismissBanner = () => {
    setBannerDismissed(true);
    sessionStorage.setItem('fcm_prompt_dismissed', 'true');
  };

  // Global prompt is disabled because notifications are managed exclusively in Support Chat area
  return null;
};

export default PushNotificationManager;
